import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Key,
  Plus,
  Search,
  MoreVertical,
  RefreshCw,
  Copy,
  Check,
  Clock,
  XCircle,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLicensesPage = () => {
  const { getAuthHeader } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // New license form
  const [newLicense, setNewLicense] = useState({
    customer_name: "",
    customer_email: "",
    company: "",
    plan: "pro",
    duration_days: 30,
    price_paid: 49,
    billing_cycle: "monthly",
    affiliate_code: ""
  });

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/admin/licenses?${params}`, {
        headers: getAuthHeader()
      });
      setLicenses(response.data.licenses);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load licenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [statusFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLicense.customer_name || !newLicense.customer_email) {
      toast.error("Please fill required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/licenses`, newLicense, {
        headers: getAuthHeader()
      });
      toast.success("License created!");
      setDialogOpen(false);
      setNewLicense({
        customer_name: "",
        customer_email: "",
        company: "",
        plan: "pro",
        duration_days: 30,
        price_paid: 49,
        billing_cycle: "monthly",
        affiliate_code: ""
      });
      fetchLicenses();
      
      // Copy the new license key
      await navigator.clipboard.writeText(response.data.license_key);
      toast.success("License key copied to clipboard!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create license");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtend = async (licenseId, days = 30) => {
    try {
      await axios.post(`${API}/admin/licenses/${licenseId}/extend?days=${days}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`License extended by ${days} days`);
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to extend license");
    }
  };

  const handleRevoke = async (licenseId) => {
    try {
      await axios.post(`${API}/admin/licenses/${licenseId}/revoke`, {}, {
        headers: getAuthHeader()
      });
      toast.success("License revoked");
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to revoke license");
    }
  };

  const handleReactivate = async (licenseId) => {
    try {
      await axios.post(`${API}/admin/licenses/${licenseId}/reactivate?extend_days=30`, {}, {
        headers: getAuthHeader()
      });
      toast.success("License reactivated");
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to reactivate license");
    }
  };

  const copyLicenseKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      toast.success("License key copied!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      expired: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
      suspended: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      revoked: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[status] || variants.expired;
  };

  const getDaysUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const filteredLicenses = licenses.filter(l => 
    !search || 
    l.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    l.customer_email.toLowerCase().includes(search.toLowerCase()) ||
    l.license_key.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-licenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Key className="w-7 h-7 text-primary" />
            Licenses
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {total} total licenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLicenses} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="create-license-btn">
                <Plus className="w-4 h-4" />
                Create License
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle>Create New License</DialogTitle>
                <DialogDescription>
                  Generate a license key for a customer.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input
                      value={newLicense.customer_name}
                      onChange={(e) => setNewLicense({...newLicense, customer_name: e.target.value})}
                      className="bg-background"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newLicense.customer_email}
                      onChange={(e) => setNewLicense({...newLicense, customer_email: e.target.value})}
                      className="bg-background"
                      data-testid="customer-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={newLicense.company}
                    onChange={(e) => setNewLicense({...newLicense, company: e.target.value})}
                    className="bg-background"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Select value={newLicense.plan} onValueChange={(v) => setNewLicense({...newLicense, plan: v})}>
                      <SelectTrigger className="bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (days)</Label>
                    <Input
                      type="number"
                      value={newLicense.duration_days}
                      onChange={(e) => setNewLicense({...newLicense, duration_days: parseInt(e.target.value)})}
                      className="bg-background"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price Paid ($)</Label>
                    <Input
                      type="number"
                      value={newLicense.price_paid}
                      onChange={(e) => setNewLicense({...newLicense, price_paid: parseFloat(e.target.value)})}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select value={newLicense.billing_cycle} onValueChange={(v) => setNewLicense({...newLicense, billing_cycle: v})}>
                      <SelectTrigger className="bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Affiliate Code (optional)</Label>
                  <Input
                    value={newLicense.affiliate_code}
                    onChange={(e) => setNewLicense({...newLicense, affiliate_code: e.target.value})}
                    placeholder="Enter affiliate ID if referred"
                    className="bg-background"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="confirm-create-license">
                    Create License
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or key..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-background">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Table */}
      <Card className="bg-card/50 border-border" data-testid="licenses-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">License Key</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Expires</th>
                  <th className="text-left px-4 py-3 hidden xl:table-cell">Revenue</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="7" className="px-4 py-3">
                        <div className="skeleton h-10 w-full" />
                      </td>
                    </tr>
                  ))
                ) : filteredLicenses.length > 0 ? (
                  filteredLicenses.map((license) => {
                    const days = getDaysUntilExpiry(license.expires_at);
                    return (
                      <tr key={license.id}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{license.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{license.customer_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-secondary px-2 py-1 rounded font-mono">
                              {license.license_key.substring(0, 15)}...
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyLicenseKey(license.license_key)}
                            >
                              {copiedKey === license.license_key ? (
                                <Check className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="uppercase">{license.plan}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={getStatusBadge(license.status)}>
                            {license.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className={`text-sm ${days <= 7 ? "text-amber-500" : ""}`}>
                              {days <= 0 ? "Expired" : `${days} days`}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <span className="font-mono">${license.price_paid}</span>
                          {license.affiliate_code && (
                            <Badge variant="outline" className="ml-2 text-[10px]">Affiliate</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => copyLicenseKey(license.license_key)}>
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Key
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleExtend(license.id, 30)}>
                                <Clock className="w-4 h-4 mr-2" />
                                Extend 30 Days
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleExtend(license.id, 365)}>
                                <Clock className="w-4 h-4 mr-2" />
                                Extend 1 Year
                              </DropdownMenuItem>
                              {license.status === "active" ? (
                                <DropdownMenuItem 
                                  onClick={() => handleRevoke(license.id)}
                                  className="text-red-500"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Revoke
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleReactivate(license.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">
                      No licenses found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLicensesPage;
